import { SystemIcon } from 'legos/system-icon';
import { Anchor } from 'packages/react-nano-router';
import { createElement, FC, ReactNode } from 'react';
import { FontContainer, Button } from 'legos/typography';

import {
  StyledNavigationBar,
  NavigationBarTitle,
  BackIconLink,
  CloseIconLink,
  NavLinkIcon,
  DesktopBtnTxt
} from './navigation-bar.ccm.css';
import { useInPageFlowLayoutTheme } from './in-page-flow-layout.theme';
import { useIsReactNativeWebView } from 'packages/use-is-react-native-webview';

export interface NavigationBarProps {
  /**
   * children are styled as the NavigationBar title
   */
  children: ReactNode;

  /**
   * If specified, a left-justified icon wrapped in an anchor will be shown
   */
  backAnchor?: Anchor;

  /**
   * If specified, a right-justified icon wrapped in an anchor will be shown
   */
  cancelAnchor?: Anchor;

  /**
   * If true on ReactNativeWebView, close button should navigate back using history.back()
   * @default true
   */
  navigateBackOnClose?: boolean;
}

const DesktopTxtContainer = DesktopBtnTxt.as(Button);
const NavIcon = NavLinkIcon.as(SystemIcon);

export const NavigationBar: FC<NavigationBarProps> = ({
  children,
  backAnchor,
  cancelAnchor,
  navigateBackOnClose = true
}) => {
  const {
    backgroundColor,
    bottomBorderColor,
    titleColor
  } = useInPageFlowLayoutTheme();

  const BackBtnTxt = 'Back';

  return (
    <StyledNavigationBar.div
      $backgroundColor={backgroundColor}
      $bottomBorderColor={bottomBorderColor}
    >
      <BackAnchor backAnchor={backAnchor} BackBtnTxt={BackBtnTxt}>
        <NavIcon type="chevronLeft" color="primary" size={3} />
        <DesktopTxtContainer color="default">{BackBtnTxt}</DesktopTxtContainer>
      </BackAnchor>
      <NavigationBarTitle.span $titleColor={titleColor}>
        <FontContainer>{children}</FontContainer>
      </NavigationBarTitle.span>
      <CancelAnchor
        cancelAnchor={cancelAnchor}
        navigateBackOnClose={navigateBackOnClose}
      />
    </StyledNavigationBar.div>
  );
};

const BackAnchor: FC<{ backAnchor?: Anchor; BackBtnTxt: string }> = ({
  backAnchor,
  BackBtnTxt,
  children
}) => {
  const isReactNativeWebView = useIsReactNativeWebView();
  const urlsForReactNativeBack = [
    '/account/gateway/r/dashboard',
    '/account/dashboard',
    '/account/gateway/r/login',
    '/account/login'
  ];

  const isYourAccount = backAnchor?.href === '/account/your-account';

  function simulateLinkClick(anchor: Anchor) {
    const a = document.createElement('a');
    a.href = anchor.href;
    // @ts-ignore type checking for event handler param
    a.onclick = anchor.onClick;
    document.body.appendChild(a); // The element must be in the DOM to be clickable
    a.click();
    document.body.removeChild(a); // Clean up by removing the element
  }

  const rnBackHandler = () => {
    const isReactNativeGoBackParam = location.href.includes(
      'reactNativeGoBack'
    );

    const hasUrlForRnBack = urlsForReactNativeBack.some(
      (url) => backAnchor?.href.indexOf(url) !== -1
    );

    if (
      hasUrlForRnBack ||
      isReactNativeGoBackParam ||
      isYourAccount ||
      window.history.length === 1 // for webview
    ) {
      (window as any)?.ReactNativeWebView.postMessage(
        JSON.stringify({ goBack: true })
      );
    } else {
      backAnchor ? simulateLinkClick(backAnchor) : history.back();
    }
  };

  return backAnchor ? (
    isReactNativeWebView ? (
      <BackIconLink.a
        onClick={() => rnBackHandler()}
        href="javascript:void(0);"
        title={BackBtnTxt}
      >
        {children}
      </BackIconLink.a>
    ) : (
      <BackIconLink.a {...backAnchor} title={BackBtnTxt}>
        {children}
      </BackIconLink.a>
    )
  ) : (
    <BackIconLink.span $empty />
  );
};

const CancelAnchor: FC<{
  cancelAnchor?: Anchor;
  navigateBackOnClose?: boolean;
}> = ({ cancelAnchor, navigateBackOnClose }) => {
  const isReactNativeWebView = useIsReactNativeWebView();

  return cancelAnchor ? (
    isReactNativeWebView && navigateBackOnClose ? (
      <CloseIconLink.a
        onClick={() => history.back()}
        href="javascript:void(0);"
        title="Cancel"
      >
        <NavIcon type="closeX" size={2} />
      </CloseIconLink.a>
    ) : (
      <CloseIconLink.a {...cancelAnchor} title="Cancel">
        <NavIcon type="closeX" size={2} />
      </CloseIconLink.a>
    )
  ) : (
    <CloseIconLink.span $empty />
  );
};
